import { Component, HostBinding, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { routePathName, routeUrl } from '@app/app.models';
import { I18nService } from '@localize/services/';
import { UsersService } from '@services/users';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback/';
import { FormsService } from '@services/forms/';
import { ProgressIndicatorService } from '@services/progress/';
import { slideInOutAnimation } from '@shared/animations';
import { FormAsyncValidators } from '@tcp13equals2/z5-lib';
import { finalize } from 'rxjs/operators';

import { FormEditorInputType } from '@app/shared/components';

@Component({
	selector: 'app-reset-password',
	templateUrl: './reset-password.component.html',
	styleUrls: ['./reset-password.component.scss'],
	animations: [slideInOutAnimation]
})
export class ResetPasswordComponent implements OnInit {
	@HostBinding('@slideInOutAnimation')
	public requestInputs = new FormGroup({
		email: new FormControl('', [Validators.required, Validators.email])
	});

	public resetInputs = new FormGroup({
		email: new FormControl('', [Validators.required, Validators.email]),
		password: new FormControl('', [Validators.required], [this.formAsyncValidators.password.bind(this.formAsyncValidators)]),
		password2: new FormControl({ value: '', disabled: true }, [Validators.required, this.formsService.confirmPasswordMatcher()])
	});

	public formEditorInputType = FormEditorInputType;
	public hidePassword = true;
	public code: string;
	public requested = false;

	constructor(
		private router: Router,
		private activeRoute: ActivatedRoute,
		private usersService: UsersService,
		private feedbackService: FeedbackService,
		private progressIndicatorService: ProgressIndicatorService,
		private formsService: FormsService,
		private i18nService: I18nService,
		private formAsyncValidators: FormAsyncValidators
	) {}

	public ngOnInit(): void {
		this.activeRoute.queryParams.subscribe((params) => {
			this.code = params?.token ? params.token : null;
		});

		this.resetInputs.get('password').statusChanges.subscribe((status: string) => {
			if (status === 'VALID') {
				this.resetInputs.get('password2').enable();
			} else {
				this.resetInputs.get('password2').reset();
				this.resetInputs.get('password2').disable();
			}
		});
	}

	public requestResetPassword(): void {
		if (this.requestInputs.invalid) {
			this.requestInputs.controls.email.markAsDirty();
			this.requestInputs.controls.email.updateValueAndValidity();
			return;
		}

		const requestResetErrorMsg = this.i18nService.get(
			'There was an error requesting your password reset.',
			'app.api.response.password-reset-request-error'
		);

		this.progressIndicatorService.spinner(true);

		this.usersService
			.sendResetPasswordEmail(this.requestInputs.get('email').value)
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe(
				(success: boolean) => {
					this.requested = true;

					this.feedbackService.plain(
						success
							? {
									title: FeedbackTitle.success,
									message: this.i18nService.get(
										'Success, you will receive an email containing a reset link.',
										'app.api.response.password-reset-request-success'
									),
									style: FeedbackStyle.success
							  }
							: {
									title: FeedbackTitle.attention,
									message: requestResetErrorMsg,
									style: FeedbackStyle.error
							  }
					);
				},
				(error: any) => {
					this.feedbackService.apiError(error);
				}
			);
	}

	public resetPassword(): void {
		if (this.resetInputs.invalid) {
			this.resetInputs.controls.email.markAsDirty();
			this.resetInputs.controls.password.markAsDirty();
			this.resetInputs.controls.password2.markAsDirty();
			this.resetInputs.updateValueAndValidity();
			return;
		}

		const resetErrorMsg = this.i18nService.get('There was an error resetting your password.', 'app.api.response.password-reset-error');

		this.progressIndicatorService.spinner(true);

		this.usersService
			.recoverPassword(this.resetInputs.get('email').value, this.resetInputs.get('password').value, this.code)
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe(
				(success: boolean) => {
					if (success) {
						this.router.navigate(routeUrl(routePathName.signIn));
					} else {
						this.feedbackService.apiError({}, resetErrorMsg);
					}
				},
				(error: any) => this.feedbackService.apiError(error, resetErrorMsg)
			);
	}
}
